import { CommonModule } from '@angular/common';
import { Component, computed, Directive, effect, inject, input, output, TemplateRef, viewChild } from '@angular/core';

//dx
import { DxButtonGroupModule, DxButtonModule, DxDataGridComponent, DxDataGridModule, DxLookupModule, DxTextBoxModule } from 'devextreme-angular';
import { DataChange, Column as DxDataGridColumn, Editing, RowPreparedEvent, Summary } from 'devextreme/ui/data_grid';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import DataSource from 'devextreme/data/data_source';
import { ArrayStore } from 'devextreme/common/data';

import { ItemClickEvent } from 'devextreme/ui/button_group';
import { MovementRequestStatus, MovementTypeCode, MovementTypeCodes, normalizeSelect, SanitizerWrapperService, StoreService } from '../../lib';
import { PaginationComponent } from '../pagination/pagination.component';
import { FilterOption, hasValue, SortExpr } from '@my-aon/utils';

export interface ITemplateStyle {
    class: string;
    displayValue: any;
    tooltip: string;
    icon?: string;
}

export const getRequestStatusStyles = (
    value: string,
    translateService: TranslateService,
    basePath: string = 'HEALTH.REQUESTS.MOVEMENT.STATUS'
): ITemplateStyle => {
    let badgeClass = (() => {
        switch (value) {
            case MovementRequestStatus.Rejected:
            case MovementRequestStatus.Error:
            case MovementRequestStatus.InsurerRejected:
            case MovementRequestStatus.InsurerError:
                return 'text-bg-danger';
            case MovementRequestStatus.ValidatedPartial:
                return 'text-bg-warning';
            case MovementRequestStatus.InsurerProcessed:
            case MovementRequestStatus.Validated:
                return 'text-bg-success';
            default:
                return 'text-bg-secondary';
        }
    })();

    const tooltipKey = `${basePath}.${value}_TOOLTIP`;
    let tooltip = translateService.instant(tooltipKey);
    if (tooltip == tooltipKey) tooltip = '';

    return {
        class: badgeClass,
        displayValue: translateService.instant(`${basePath}.${value}`),
        tooltip,
    };
};

export const cellTemplateRequestStatus = (
    translateService: TranslateService,
    sanitizer: SanitizerWrapperService,
    basePath: string = 'HEALTH.REQUESTS.MOVEMENT.STATUS'
) => {
    return (elem: HTMLElement, cellInfo: any) => {
        let value: MovementRequestStatus = cellInfo.displayValue ?? cellInfo.value;
        let styles: ITemplateStyle = getRequestStatusStyles(value, translateService, basePath);
        elem.innerHTML = `<span class="badge ${styles.class}" title="${styles.tooltip}">${sanitizer.sanitize(styles.displayValue)}</span>`;
    };
};


export const getMovementTypeStyles = (
    value: MovementTypeCode | 'MIXED',
    translateService: TranslateService,
    basePath: string = 'HEALTH.ENROLLMENTS.MOVEMENT_TYPES'
): ITemplateStyle => {

    console.log(value)

    let badgeClass = (() => {
        switch (value) {
            case MovementTypeCodes.Inclusion:
                return 'text-bg-success';
            case MovementTypeCodes.Change:
                return 'text-bg-warning';
            case MovementTypeCodes.Exclusion:
                return 'text-bg-danger';
            case 'MIXED':
                return 'text-bg-dark';
            default:
                return 'text-bg-secondary';
        }
    })();

    let iconClass = (() => {
        switch (value) {
            case MovementTypeCodes.Inclusion:
                return 'fa-solid fa-user-plus';
            case MovementTypeCodes.Change:
                return 'fa-solid fa-user-pen';
            case MovementTypeCodes.Exclusion:
                return 'fa-solid fa-user-xmark';
            default:
                return '';
        }
    })();

    return {
        class: badgeClass,
        displayValue: translateService.instant(`${basePath}.${value}`),
        tooltip: '',
        icon: iconClass
    };
};

export const cellTemplateMovementType = (
    translateService: TranslateService,
    sanitizer: SanitizerWrapperService,
    basePath: string = 'HEALTH.ENROLLMENTS.MOVEMENT_TYPES'
) => {
    return (elem: HTMLElement, cellInfo: any) => {
        let styles = getMovementTypeStyles(cellInfo.displayValue, translateService, basePath);

        elem.innerHTML = `<span class="badge ${styles.class}">${sanitizer.sanitize(styles.displayValue)}</span>`;
    };
};

@Directive({})
export abstract class DataGridDirective<TRowData = any> {

    readonly storeService: StoreService = inject(StoreService);
    readonly translateService = inject(TranslateService);
    readonly sanitizer = inject(SanitizerWrapperService);

    $dataGrid = viewChild.required(DxDataGridComponent);

    dataSource: DataSource<TRowData> | ArrayStore<TRowData> | any[] = [];

    changes: DataChange<TRowData>[] = [];

    clearSelection() {
        this.$dataGrid()?.instance?.clearSelection();
    }

    clearSorting() {
        this.$dataGrid()?.instance?.clearSorting();
    }

    public refresh() {

        this.clearSelection();

        this.$dataGrid()?.instance?.refresh();

        if ('reload' in this.dataSource)
            this.dataSource?.reload();
    }

    saveEditData = () => this.$dataGrid()?.instance?.saveEditData();

}

@Component({
    selector: 'app-data-grid',
    templateUrl: './data-grid.component.html',
    styleUrl: './data-grid.component.scss',
    imports: [
        CommonModule,
        TranslatePipe,
        PaginationComponent,
        DxDataGridModule,
        DxButtonModule,
        DxLookupModule,
        DxButtonGroupModule,
        DxTextBoxModule
    ],
    host: {
        'class': 'd-block h-100 w-100'
    }
})
export class DataGridComponent extends DataGridDirective {

    $columns = input<DxDataGridColumn[]>([], { alias: 'columns' });
    $summary = input<Summary>({}, { alias: 'summary' });

    public readonly $url = input<string | undefined>(undefined, { alias: 'url' });
    public readonly $localDataSource = input<any[] | undefined>(undefined, {
        alias: 'dataSource',
    });

    $key = input<string | string[]>('Id', { alias: 'key' });
    $groupBy = input<any>(null, { alias: 'groupBy' });

    $onRowPrepared = output<RowPreparedEvent>({ alias: 'onRowPrepared' })

    $columnAutoWidth = input<boolean>(false, { alias: 'columnAutoWidth' });

    $allowHeaderFiltering = input<boolean>(false, { alias: 'allowHeaderFiltering' });

    $rowTemplate = input<TemplateRef<any> | null>(null, { alias: 'rowTemplate' });
    $cellTemplates = input<{ name: string, template: TemplateRef<any> }[]>([], { alias: 'cellTemplates' });

    $filterOptions = input<Array<FilterOption> | undefined>(undefined, { alias: 'filterOptions' })

    $sortingOptions = input<Array<{ label: string, sortExpr: SortExpr }> | undefined>(undefined, { alias: 'sortingOptions' });

    $disableAutoSelect = input<boolean>(false, { alias: 'disableAutoSelect' });

    // Adding
    $allowAdding = input<boolean>(false, { alias: 'allowAdding' });
    $addingButtonText = input<string>('COMMON.ACTION_NEW', { alias: 'addingButtonText' });
    $addingButtonIcon = input<string>('fa-solid fa-plus fa-fw', { alias: 'addingButtonIcon' });
    onAddingClick = output<any>();

    $editing = input<Editing>({}, { alias: 'editing' });

    $filterPanel = input<boolean>(false, { alias: 'filterPanel' });
    $filterRow = input<boolean>(false, { alias: 'filterRow' });
    $headerFilter = input<boolean>(false, { alias: 'headerFilter' });

    $showRefresh = computed<boolean>(() => hasValue(this.$url()));

    constructor() {

        super();

        effect(() => {
            const localDataSource = this.$localDataSource();
            if (localDataSource) {
                this.dataSource = localDataSource;
                return;
            }

            let url = this.$url();
            if (!url) return;

            let select = this.$disableAutoSelect() ? undefined : [this.$key(), ...normalizeSelect(this.$columns())];
            let groupBy = this.$groupBy();
            const defaultSort = this.$sortingOptions()?.[0];
            if (defaultSort && !this.currentSort) {
                this.currentSort = defaultSort.label;
            }
            const sort = defaultSort?.sortExpr
                ? (Array.isArray(defaultSort.sortExpr) ? defaultSort.sortExpr : [defaultSort.sortExpr])
                : null;
            this.dataSource = this.storeService.DataSource(url, this.$key(), {
                select: select,
                group: groupBy,
                sort: sort as any,
            });
        })

        effect(() => {
            let filterOptions = this.$filterOptions();
            filterOptions?.forEach(x => {
                if (x.editorType != 'dxButtonGroup') return;
                x.dataSource.forEach((y: { height: number; width: number; }) => {
                    y.height = 48;
                    y.width = 48;
                })
            })
        })
    }

    onRowPrepared(e: RowPreparedEvent) {
        this.$onRowPrepared.emit(e);
    }



    get hasFilters(): boolean {
        return this.$dataGrid().instance.getDataSource().filter()?.length > 0 || !!this.$dataGrid().instance.getCombinedFilter()
    }
    clearFilters() {
        this.$dataGrid().instance.clearFilter();
        this.$filterOptions()?.forEach(item => {
            item.value = undefined;
        })
    }

    onFilter() {

        let dataSource = this.$dataGrid().instance.getDataSource();

        let filterExpr: any[] = [];
        this.$filterOptions()?.forEach(x => {
            const val = x.value;
            if (Array.isArray(val)) {
                if (val.length) {
                    filterExpr.push(val.map(v => {
                        let subFilter = [x.dataField, v];
                        // Custom filter (Instead of item's datafield + specific value, its an advanced filter that may take other datafields)
                        // eg.: datafield: 'Gender', value: 'M' *VS* datafield: 'RelationshipCode', value: ['IsFicticious', true]
                        if (Array.isArray(v))
                            subFilter = v;
                        return [subFilter, 'or']
                    }).flat().slice(0, -1))
                }
            }
            else if (val != undefined) {
                filterExpr.push([x.dataField, val])
            }
        })

        if (filterExpr && filterExpr?.length > 0)
            dataSource.filter(filterExpr);
        else
            this.$dataGrid().instance.clearFilter();

        dataSource.reload();
    }

    onSelectionChanged() {
        this.onFilter();
    }

    onItemClick($e: ItemClickEvent, item: FilterOption) {
        if (!Array.isArray(item.value))
            item.value = [];
        const selectionMode = item.selectionMode ?? 'single'
        if (selectionMode == 'single') {
            // If single selection, disable any other filters than what was clicked.
            item.value = item.value.filter((x: any) => x == $e.itemData.value);
        }
        // Toggle
        if (!item.value.includes($e.itemData.value))
            item.value.push($e.itemData.value)
        else
            item.value.splice(item.value.indexOf($e.itemData.value), 1)
        this.onFilter();
    }

    currentSort?: string;
    onSort(key: string, sortExpr: SortExpr) {
        this.currentSort = key;

        let dataSource = this.$dataGrid()?.instance?.getDataSource();
        this.$dataGrid()?.instance?.clearSorting();
        if (dataSource) {
            dataSource.sort([]);
            dataSource.sort(sortExpr);
            dataSource.reload();
        }

    }

    public get pageSize() {
        return this.$dataGrid().instance.pageSize();
    }
    public set pageSize(value) {
        this.$dataGrid().instance.pageSize(value);
    }

    public get currentPage() {
        return this.$dataGrid().instance.pageIndex() + 1;
    }
    public set currentPage(value) {
        this.$dataGrid().instance.pageIndex(value - 1);
    }

    applyDefaultColumnCss = (c: DxDataGridColumn | string) => {
        if (typeof c == 'string') return c as any;
        c.cssClass ??= 'p-1'
        if (c.columns?.length) {
            c.columns = c.columns.map(this.applyDefaultColumnCss)
        }
        return c;
    }

    columns = computed<DxDataGridColumn[]>(() => this.$columns().map(this.applyDefaultColumnCss))

}
